import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { Group, Stack, Text } from '@mantine/core';
import type { SerializedEditorState } from 'lexical';
import type { FC } from 'react';
import { useMemo } from 'react';
import Editor from '@/components/editor';
import { DiffNode } from '@/components/editor/components/highlight-difference-plugin/diff-node.tsx';
import { MentionNode } from '@/components/editor/components/mention';
import useFlowStore from '@/store/flow';
import type { ChangeSet } from '@/types/common.ts';
import {
	editorValueToMarkdown,
	normalizeEditorValue,
	plainTextToEditorState
} from '@/utils/editor-conversion';

type FlowItemTitleEditorProps = {
	isDisabled: boolean;
	value: SerializedEditorState | string;
	onChange: (value: SerializedEditorState) => void;
	changeSet: ChangeSet;
	isRemoveActive?: boolean;
	isAddedActive?: boolean;
};

export const FlowItemTitleEditor: FC<FlowItemTitleEditorProps> = ({
	isDisabled,
	value,
	onChange,
	changeSet,
	isRemoveActive,
	isAddedActive
}) => {
	const isFormattedView = useFlowStore(
		(state) => state.flowDrawer.isFormattedView
	);

	const initialEditorState = useMemo(
		() =>
			isFormattedView
				? normalizeEditorValue(value)
				: plainTextToEditorState(editorValueToMarkdown(value)),
		[isFormattedView, value]
	);

	const initialConfig = {
		namespace: 'MyEditor',
		theme: {},
		onError: console.error,
		nodes: [
			DiffNode,
			HeadingNode,
			ListNode,
			ListItemNode,
			QuoteNode,
			CodeNode,
			CodeHighlightNode,
			LinkNode,
			AutoLinkNode,
			MentionNode
		],
		editable: !isDisabled,
		editorState: JSON.stringify(initialEditorState)
	};

	return (
		<Stack
			gap={4}
			w="100%"
		>
			<Text
				fw={500}
				size="sm"
			>
				Title
			</Text>

			<Group
				h="100%"
				justify="space-between"
				w="100%"
				wrap="nowrap"
			>
				<LexicalComposer initialConfig={initialConfig}>
					<Editor
						changeSet={changeSet}
						isAddedActive={isAddedActive}
						isDisabled={isDisabled}
						isRemoveActive={isRemoveActive}
						isTitle
						onChange={onChange}
						value={value}
					/>
				</LexicalComposer>
			</Group>
		</Stack>
	);
};
